import {
  WorkplaceRequestPublicDashboardLink,
  WorkplaceRequestPublicDashboardLinkCreateParams,
  WorkplaceRequestPublicDashboardLinkParams,
  WorkplaceRequestPublicDashboardLinksParams,
  WorkplaceRequestPublicDashboardLinksResponse
} from "../types/workplace-request-dashboards";
import { BaseSettingsEndpoints } from "./base";

const WORKPLACE_REQUEST_DASHBOARDS_BASE_PATH = "/v1/tickets/public-dashboards";

/**
 * Manage the public dashboard links of an organization. These calls are
 * authenticated as a signed-in user: organization owners may manage every link,
 * and a request-type manager may manage the links whose types they manage —
 * both scoped further by the offices they can reach.
 *
 * The guest-facing half of the feature lives in
 * {@link WorkplaceRequestDashboardPublicClient}.
 */
export class WorkplaceRequestDashboardsClient extends BaseSettingsEndpoints {
  /**
   * Create a public dashboard link. The response carries the `token` that the
   * public page presents; it is only ever returned by the API, never chosen.
   *
   * Fails with `types_not_managed` / `offices_not_managed` when the caller does
   * not manage every selected type in every selected office, and with
   * `office_not_allowed` when a selected type is not available in one of them.
   */
  async createPublicDashboardLink(
    params: WorkplaceRequestPublicDashboardLinkCreateParams
  ) {
    return this.post<
      WorkplaceRequestPublicDashboardLinkCreateParams,
      WorkplaceRequestPublicDashboardLink
    >(WORKPLACE_REQUEST_DASHBOARDS_BASE_PATH, params);
  }

  /** List the public dashboard links the caller may manage. */
  async getPublicDashboardLinks(
    params: WorkplaceRequestPublicDashboardLinksParams = {}
  ) {
    const query = this.formatGenericParams({
      cursor: params.cursor ?? null,
      limit: params.limit ?? null
    }).toString();

    const url = `${WORKPLACE_REQUEST_DASHBOARDS_BASE_PATH}${query ? `?${query}` : ""}`;

    return this.get<WorkplaceRequestPublicDashboardLinksResponse>(url);
  }

  /**
   * Replace a link's whole configuration. There is no partial update: every
   * field is sent, and anything omitted is cleared. Fails with `link_revoked`
   * (409) once the link has been revoked.
   */
  async updatePublicDashboardLink(
    linkId: string,
    params: WorkplaceRequestPublicDashboardLinkParams
  ) {
    return this.update<
      WorkplaceRequestPublicDashboardLinkParams,
      WorkplaceRequestPublicDashboardLink
    >(`${WORKPLACE_REQUEST_DASHBOARDS_BASE_PATH}/${linkId}`, params);
  }

  /**
   * Revoke a public dashboard link, cutting off access through its token
   * immediately. Revoking cannot be undone: a new link means a new URL.
   */
  async deletePublicDashboardLink(linkId: string) {
    return this.delete(`${WORKPLACE_REQUEST_DASHBOARDS_BASE_PATH}/${linkId}`);
  }
}
